import React from "react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatError, formatRupiah } from "@/lib/api";
import { Plus, Pencil, Trash2, Upload, Download, X, FileSpreadsheet, Search, ChevronLeft, ChevronRight } from "lucide-react";

const empty = { name: "", description: "", price: 0, cost: 0, stock: 0, type: "digital", category_id: "", image_url: "", sku: "" };
const PER_PAGE = 10;

export default function AdminProducts() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);

  const load = () => {
    api.get("/products").then((r) => setItems(r.data));
    api.get("/categories").then((r) => setCats(r.data));
  };
  useEffect(load, []);

  const filtered = items.filter((p) => {
    const q = search.toLowerCase();
    return p.name?.toLowerCase().includes(q) || p.sku?.toLowerCase().includes(q);
  });

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const pageData = filtered.slice(page * PER_PAGE, (page + 1) * PER_PAGE);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, price: Number(form.price), cost: Number(form.cost || 0), stock: Number(form.stock) };
      if (editing) await api.put(`/products/${editing}`, payload);
      else await api.post("/products", payload);
      toast.success("Produk disimpan");
      setForm(empty); setEditing(null); setShowForm(false); load();
    } catch (err) { toast.error(formatError(err)); }
  };

  const edit = (p) => { setForm(p); setEditing(p.id); setShowForm(true); };
  const remove = async (p) => {
    if (!window.confirm(`Hapus ${p.name}?`)) return;
    await api.delete(`/products/${p.id}`); toast.success("Dihapus"); load();
  };

  const downloadTemplate = () => {
    const headers = ["nama_toko","nama_produk","tipe","kategori_slug","harga","hpp_cost","stok","sku","url_gambar","deskripsi"];
    const contoh = ["Toko Saya","Mobile Legends 86 Diamond","digital","top-up-game","1.50","1.20","999","ML-86","https://images.unsplash.com/photo-1587829741301-dc798b83add3?w=800","Top up ML otomatis via panel master"];
    const csv = [headers.join(","), contoh.join(",")].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "template_import_produk.csv"; a.click();
    URL.revokeObjectURL(url);
    toast.success("Template berhasil didownload!");
  };

  const handleImport = async (e) => {
    e.preventDefault();
    if (!importFile) { toast.error("Pilih file dulu!"); return; }
    setImporting(true); setImportResult(null);
    try {
      const formData = new FormData();
      formData.append("file", importFile);
      const res = await api.post("/admin/products/bulk-import", formData, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(res.data);
      toast.success(res.data.message);
      load();
    } catch (err) { toast.error(formatError(err)); }
    finally { setImporting(false); }
  };

  return (
    <div data-testid="admin-products">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <div className="label-mono text-zinc-500">// CATALOG</div>
          <h1 className="font-display text-3xl md:text-4xl font-black uppercase mt-1">Produk</h1>
        </div>
        <div className="flex gap-2">
          <button onClick={() => { setShowImport(!showImport); setShowForm(false); }} className="btn-secondary inline-flex items-center gap-2">
            <Upload className="w-4 h-4" /> Import Massal
          </button>
          <button onClick={() => { setEditing(null); setForm(empty); setShowForm(!showForm); setShowImport(false); }} className="btn-primary inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> Tambah
          </button>
        </div>
      </div>

      {showImport && (
        <div className="mt-6 border border-black bg-white p-6 shadow-brutal">
          <div className="flex justify-between items-center mb-4">
            <div>
              <div className="label-mono text-zinc-500">// IMPORT MASSAL</div>
              <h2 className="font-display text-xl font-black uppercase mt-1">Upload File Excel / CSV</h2>
            </div>
            <button onClick={() => { setShowImport(false); setImportResult(null); setImportFile(null); }}><X className="w-5 h-5" /></button>
          </div>
          <button onClick={downloadTemplate} className="mb-4 px-4 py-2 border border-black font-mono text-xs font-bold uppercase flex items-center gap-2 hover:bg-black hover:text-white">
            <Download className="w-4 h-4" /> Download Template CSV
          </button>
          <form onSubmit={handleImport}>
            <label className="label-mono block mb-2">Pilih File (.xlsx atau .csv)</label>
            <div className="border-2 border-dashed border-black p-6 text-center mb-4 hover:bg-zinc-50 cursor-pointer" onClick={() => document.getElementById("import-file").click()}>
              <FileSpreadsheet className="w-8 h-8 mx-auto mb-2 text-zinc-400" />
              {importFile ? (
                <div className="font-mono text-sm font-bold text-[#00C853]">✓ {importFile.name}</div>
              ) : (
                <div className="font-mono text-xs text-zinc-500">Klik untuk pilih file .xlsx atau .csv</div>
              )}
            </div>
            <input id="import-file" type="file" accept=".xlsx,.csv" onChange={(e) => { setImportFile(e.target.files[0]); setImportResult(null); }} className="hidden" />
            <button type="submit" disabled={importing || !importFile} className="btn-primary w-full flex items-center justify-center gap-2">
              <Upload className="w-4 h-4" />{importing ? "// MENGIMPORT..." : "Import Produk Sekarang"}
            </button>
          </form>
          {importResult && (
            <div className="mt-4 border border-black p-4">
              <div className="label-mono mb-2">// HASIL IMPORT</div>
              <div className="grid grid-cols-3 gap-3">
                <div className="border border-[#00C853] bg-green-50 p-3 text-center">
                  <div className="font-display text-2xl font-black text-[#00C853]">{importResult.success}</div>
                  <div className="label-mono text-[10px] text-zinc-500">BERHASIL</div>
                </div>
                <div className="border border-[#FF3333] bg-red-50 p-3 text-center">
                  <div className="font-display text-2xl font-black text-[#FF3333]">{importResult.errors?.length || 0}</div>
                  <div className="label-mono text-[10px] text-zinc-500">ERROR</div>
                </div>
                <div className="border border-black bg-zinc-50 p-3 text-center">
                  <div className="font-display text-2xl font-black">{importResult.total}</div>
                  <div className="label-mono text-[10px] text-zinc-500">TOTAL</div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {showForm && (
        <form onSubmit={save} className="mt-6 border border-black bg-white p-6 grid md:grid-cols-2 gap-4 shadow-brutal">
          <div><label className="label-mono block mb-2">Nama</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-brutal" required /></div>
          <div><label className="label-mono block mb-2">SKU</label><input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} className="input-brutal" /></div>
          <div><label className="label-mono block mb-2">Tipe</label>
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="input-brutal">
              <option value="digital">Digital</option><option value="physical">Fisik</option>
            </select>
          </div>
          <div><label className="label-mono block mb-2">Kategori</label>
            <select value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })} className="input-brutal" required>
              <option value="">-- pilih --</option>
              {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div><label className="label-mono block mb-2">Harga ($)</label><input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="input-brutal" required /></div>
          <div><label className="label-mono block mb-2">HPP / Cost ($)</label><input type="number" step="0.01" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} className="input-brutal" /></div>
          <div><label className="label-mono block mb-2">Stok</label><input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="input-brutal" /></div>
          <div>
            <label className="label-mono block mb-2">Gambar Produk</label>
            <div className="border border-black p-3 bg-zinc-50">
              <input type="file" accept="image/*" onChange={async (e) => {
                if (!e.target.files[0]) return;
                const file = e.target.files[0];
                const fd = new FormData();
                fd.append("file", file); fd.append("upload_preset", "ml_default");
                try {
                  const res = await fetch("https://api.cloudinary.com/v1_1/dcstha9qi/image/upload", { method: "POST", body: fd });
                  const data = await res.json();
                  if (data.secure_url) { setForm((prev) => ({ ...prev, image_url: data.secure_url })); toast.success("Gambar diupload!"); }
                } catch { toast.error("Gagal upload gambar"); }
              }} className="hidden" id="admin-img-upload" />
              <label htmlFor="admin-img-upload" className="cursor-pointer flex items-center gap-2 font-mono text-xs font-bold hover:text-zinc-600">
                <Upload className="w-4 h-4" /> Pilih Gambar
              </label>
              {form.image_url && <img src={form.image_url} alt="preview" className="w-24 h-24 object-cover border border-black mt-2" />}
            </div>
          </div>
          <div className="md:col-span-2"><label className="label-mono block mb-2">Deskripsi</label><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="input-brutal" required /></div>
          <div className="md:col-span-2 flex gap-3">
            <button type="submit" className="btn-primary">{editing ? "Update" : "Simpan"}</button>
            <button type="button" onClick={() => { setShowForm(false); setEditing(null); setForm(empty); }} className="btn-secondary">Batal</button>
          </div>
        </form>
      )}

      <div className="mt-6 border border-black bg-white overflow-x-auto">
        <div className="p-3 border-b border-black flex flex-wrap justify-between items-center gap-2">
          <div className="flex items-center gap-3">
            <div className="label-mono text-zinc-500">{filtered.length} produk</div>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} placeholder="Cari nama / SKU..." className="input-brutal pl-8 py-1.5 text-xs w-52" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] text-zinc-500">{page * PER_PAGE + 1}–{Math.min((page + 1) * PER_PAGE, filtered.length)} dari {filtered.length}</span>
            <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
              className="p-1.5 border border-black hover:bg-black hover:text-white disabled:opacity-30">
              <ChevronLeft className="w-3 h-3" />
            </button>
            <span className="font-mono text-[10px]">{page + 1}/{totalPages || 1}</span>
            <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
              className="p-1.5 border border-black hover:bg-black hover:text-white disabled:opacity-30">
              <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-black">
            <tr>
              <th className="p-3 text-left label-mono">PRODUK</th>
              <th className="p-3 text-left label-mono">TIPE</th>
              <th className="p-3 text-right label-mono">HARGA</th>
              <th className="p-3 text-right label-mono">STOK</th>
              <th className="p-3 text-right label-mono">AKSI</th>
            </tr>
          </thead>
          <tbody>
            {pageData.map((p) => (
              <tr key={p.id} className="border-b border-zinc-200">
                <td className="p-3 flex items-center gap-3">
                  {p.image_url ? <img src={p.image_url} alt="" className="w-10 h-10 object-cover border border-black" />
                    : <div className="w-10 h-10 border border-black bg-zinc-100 flex items-center justify-center font-mono text-[8px] text-zinc-400">NO IMG</div>}
                  <div>
                    <div className="font-mono text-xs font-bold">{p.name}</div>
                    <div className="text-[10px] text-zinc-500 font-mono">{p.sku}</div>
                  </div>
                </td>
                <td className="p-3"><span className="badge-status bg-white">{p.type === "digital" ? "DIGITAL" : "FISIK"}</span></td>
                <td className="p-3 text-right font-mono text-xs">{formatRupiah(p.price)}</td>
                <td className="p-3 text-right font-mono text-xs">{p.stock}</td>
                <td className="p-3 text-right">
                  <button onClick={() => edit(p)} className="p-2 border border-black hover:bg-black hover:text-white mr-2"><Pencil className="w-3 h-3" /></button>
                  <button onClick={() => remove(p)} className="p-2 border border-black hover:bg-[#FF3333] hover:text-white"><Trash2 className="w-3 h-3" /></button>
                </td>
              </tr>
            ))}
            {pageData.length === 0 && (
              <tr><td colSpan={5} className="p-8 text-center font-mono text-xs text-zinc-500">// {search ? `tidak ada produk "${search}"` : "belum ada produk"}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}